import { useState, useEffect } from 'react';
import { Button } from '@paulownia/shared/components/ui/button';
import { Label } from '@paulownia/shared/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@paulownia/shared/components/ui/dialog';
import { Checkbox } from '@paulownia/shared/components/ui/checkbox';
import { LazyImage, getProductImageUrl } from '@paulownia/shared';
import { Package, Plus } from 'lucide-react';
import {
    getEffectiveSaleMode,
    isUnitCountSaleMode,
    getCompositionOption,
    safeToFixed,
    type OrderLineOption,
    type CompositionSelection,
    type SaleMode
} from '@paulownia/shared/types';
import type { ProductAddModalProps } from './types';
import { CompositionSelector } from './CompositionSelector';
import { PriceDisplay } from './PriceDisplay';
import { QuantitySelector } from './QuantitySelector';
import { calculateEstimatedPrice, getOrderLineQuantity } from './sale-mode-utils';

// Type for selected options state: boolean for boolean options, string for select, string[] for multiselect
type SelectedOptionsState = Record<string, boolean | string | string[]>;

import type { ProductOption } from '@paulownia/shared/types';

// Calculate total price modifier from selected options
function calculateOptionsPriceModifier(
    selectedOptions: SelectedOptionsState,
    availableOptions: ProductOption[]
): number {
    let total = 0;

    for (const [optionId, value] of Object.entries(selectedOptions)) {
        const option = availableOptions.find(o => o.id === optionId);
        if (!option || !option.choices) continue;

        if (option.type === 'select' && typeof value === 'string') {
            const choice = option.choices.find(c => c.id === value);
            if (choice?.priceModifier) {
                total += choice.priceModifier;
            }
        } else if (option.type === 'multiselect' && Array.isArray(value)) {
            for (const choiceId of value) {
                const choice = option.choices.find(c => c.id === choiceId);
                if (choice?.priceModifier) {
                    total += choice.priceModifier;
                }
            }
        }
    }

    return total;
}

// Default serving options for WEIGHT_ESTIMATED products
const DEFAULT_SERVINGS = 4;

export function ProductAddModal({
    open,
    product,
    products,
    onClose,
    onAddToCart
}: ProductAddModalProps) {
    const [selectedOptions, setSelectedOptions] = useState<SelectedOptionsState>({});
    const [quantity, setQuantity] = useState(1);
    const [servings, setServings] = useState(DEFAULT_SERVINGS);
    const [selectedPortionSizeId, setSelectedPortionSizeId] = useState<string | null>(null);
    const [compositionSelections, setCompositionSelections] = useState<CompositionSelection[]>([]);

    // Get sale mode for current product
    const saleMode: SaleMode = product ? getEffectiveSaleMode(product) : 'UNIT';

    // Reset state when product changes
    useEffect(() => {
        if (product) {
            const mode = getEffectiveSaleMode(product);

            // Initialize quantity based on sale mode
            switch (mode) {
                case 'UNIT':
                case 'UNIT_PRICED_BY_WEIGHT':
                    setQuantity(1);
                    setSelectedPortionSizeId(null);
                    break;
                case 'WEIGHT':
                    setQuantity(0.5);
                    setSelectedPortionSizeId(null);
                    break;
                case 'WEIGHT_ESTIMATED':
                    setServings(DEFAULT_SERVINGS);
                    setQuantity(1); // Not used for this mode
                    setSelectedPortionSizeId(null);
                    break;
                case 'WEIGHT_PACKAGED':
                    setQuantity(1);
                    // Select first portion by default
                    if (product.portionSizes && product.portionSizes.length > 0) {
                        setSelectedPortionSizeId(product.portionSizes[0].id);
                    } else {
                        setSelectedPortionSizeId(null);
                    }
                    break;
            }

            setSelectedOptions({});
            setCompositionSelections([]);
        }
    }, [product]);

    if (!product) return null;

    // Use full image URL (prefer uploaded imageUrl over legacy picture)
    const imageUrl = getProductImageUrl(product);
    const hasImage = !!imageUrl;
    const availableOptions = product.availableOptions || [];
    // Filter out composition options from regular options display
    const regularOptions = availableOptions.filter(opt => opt.type !== 'composition');
    const hasRegularOptions = regularOptions.length > 0;
    // Check for composition option
    const compositionOption = getCompositionOption(product);
    const hasComposition = !!compositionOption;
    const compositionTotal = compositionSelections.reduce((sum, s) => sum + s.quantity, 0);
    const isCompositionComplete = !hasComposition || compositionTotal === (compositionOption?.totalRequired || 0);

    // Toggle boolean option
    const handleBooleanToggle = (optionId: string) => {
        setSelectedOptions(prev => ({
            ...prev,
            [optionId]: !prev[optionId]
        }));
    };

    // Select single choice (for 'select' type)
    const handleSelectChange = (optionId: string, choiceId: string) => {
        setSelectedOptions(prev => ({
            ...prev,
            [optionId]: choiceId
        }));
    };

    // Toggle choice in multiselect
    const handleMultiselectToggle = (optionId: string, choiceId: string, maxSelections?: number) => {
        setSelectedOptions(prev => {
            const currentValue = prev[optionId];
            const currentArray = Array.isArray(currentValue) ? currentValue : [];

            if (currentArray.includes(choiceId)) {
                // Remove choice
                return {
                    ...prev,
                    [optionId]: currentArray.filter(id => id !== choiceId)
                };
            } else {
                // Add choice (respect max selections)
                if (maxSelections && currentArray.length >= maxSelections) {
                    return prev; // Don't add if at max
                }
                return {
                    ...prev,
                    [optionId]: [...currentArray, choiceId]
                };
            }
        });
    };

    // Check if a multiselect choice is disabled (max reached and not selected)
    const isMultiselectChoiceDisabled = (optionId: string, choiceId: string, maxSelections?: number): boolean => {
        if (!maxSelections) return false;
        const currentValue = selectedOptions[optionId];
        const currentArray = Array.isArray(currentValue) ? currentValue : [];
        return currentArray.length >= maxSelections && !currentArray.includes(choiceId);
    };

    // Check if all required select options have been chosen
    const areSelectOptionsValid = (): boolean => {
        for (const option of regularOptions) {
            // Only validate select options that are marked as required
            if (option.type === 'select' && option.required && option.choices && option.choices.length > 0) {
                const value = selectedOptions[option.id];
                if (!value || value === '') {
                    return false;
                }
            }
        }
        return true;
    };

    // Check if selection is valid based on sale mode
    const isSelectionValid = (): boolean => {
        switch (saleMode) {
            case 'WEIGHT_ESTIMATED':
                return servings > 0;
            case 'WEIGHT_PACKAGED':
                return quantity > 0 && selectedPortionSizeId !== null;
            default:
                return quantity > 0;
        }
    };

    const canAddToCart = isSelectionValid() && areSelectOptionsValid() && isCompositionComplete;

    // Calculate estimated price
    const baseEstimatedPrice = calculateEstimatedPrice(
        product,
        saleMode,
        quantity,
        servings,
        selectedPortionSizeId
    );

    // Calculate price modifier from selected options
    const optionsPriceModifier = calculateOptionsPriceModifier(selectedOptions, regularOptions);

    // Total estimated price including options
    // For unit-based modes, modifiers apply per unit; for weight-based, once per line
    const optionsTotal = isUnitCountSaleMode(saleMode)
        ? optionsPriceModifier * quantity
        : optionsPriceModifier;
    const estimatedPrice = baseEstimatedPrice !== null
        ? baseEstimatedPrice + optionsTotal
        : null;

    const handleAdd = () => {
        if (!canAddToCart) return;

        // Convert selectedOptions to OrderLineOption[]
        const options: OrderLineOption[] = [];

        for (const [optionId, value] of Object.entries(selectedOptions)) {
            // Skip falsy boolean values
            if (value === false || value === undefined) continue;

            // Skip empty arrays
            if (Array.isArray(value) && value.length === 0) continue;

            // Skip empty strings
            if (value === '') continue;

            options.push({ id: optionId, value });
        }

        // Add composition selections if present
        if (hasComposition && compositionOption && compositionSelections.length > 0) {
            options.push({
                id: compositionOption.id,
                value: compositionSelections.filter(s => s.quantity > 0),
            });
        }

        // Get the correct quantity based on sale mode
        const orderQuantity = getOrderLineQuantity(product, saleMode, quantity, servings);

        // Pass additional sale mode info
        // Only pass servings for WEIGHT_ESTIMATED mode
        onAddToCart(
            orderQuantity,
            options,
            selectedPortionSizeId ?? undefined,
            saleMode,
            saleMode === 'WEIGHT_ESTIMATED' ? servings : undefined
        );
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="text-xl font-bold">{product.name}</DialogTitle>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* Product Image */}
                    <div className="flex justify-center">
                        {hasImage ? (
                            <LazyImage
                                src={imageUrl!}
                                alt={product.name}
                                width={128}
                                height={128}
                                className="w-32 h-32 object-cover rounded-lg"
                                fallback={
                                    <div className="w-32 h-32 bg-gray-100 rounded-lg flex items-center justify-center">
                                        <Package className="w-16 h-16 text-gray-400" />
                                    </div>
                                }
                            />
                        ) : (
                            <div className="w-32 h-32 bg-gray-100 rounded-lg flex items-center justify-center">
                                <Package className="w-16 h-16 text-gray-400" />
                            </div>
                        )}
                    </div>

                    {/* Description */}
                    {product.description && (
                        <p className="text-sm text-gray-600 text-center">{product.description}</p>
                    )}

                    {/* Price Display - centered */}
                    <div className="flex justify-center">
                        <PriceDisplay product={product} />
                    </div>

                    {/* Disclaimer */}
                    {product.disclaimer && (
                        <p className="text-xs text-orange-600 text-center italic">{product.disclaimer}</p>
                    )}

                    {/* Composition Selector */}
                    {hasComposition && compositionOption && (
                        <div className="space-y-3 pt-2 border-t">
                            <CompositionSelector
                                compositionOption={compositionOption}
                                selections={compositionSelections}
                                products={products}
                                onSelectionsChange={setCompositionSelections}
                            />
                        </div>
                    )}

                    {/* Options */}
                    {hasRegularOptions && (
                        <div className="space-y-3 pt-2 border-t">
                            <Label className="text-base font-semibold">Options</Label>
                            <div className="space-y-4">
                                {regularOptions.map((option) => (
                                    <div key={option.id} className="space-y-2">
                                        {/* Boolean option - single checkbox */}
                                        {option.type === 'boolean' && (
                                            <label className="flex items-center gap-3 cursor-pointer p-2 rounded-lg hover:bg-gray-50 transition-colors">
                                                <Checkbox
                                                    checked={selectedOptions[option.id] === true}
                                                    onCheckedChange={() => handleBooleanToggle(option.id)}
                                                />
                                                <span className="text-sm">{option.label}</span>
                                            </label>
                                        )}

                                        {/* Select option - radio buttons */}
                                        {option.type === 'select' && option.choices && (
                                            <div className="space-y-2">
                                                <span className="text-sm font-medium text-gray-700">
                                                    {option.label}
                                                    {option.required && <span className="text-red-500 ml-1">*</span>}
                                                </span>
                                                <div className="space-y-1">
                                                    {option.choices.map((choice) => (
                                                        <label
                                                            key={choice.id}
                                                            className="flex items-center gap-3 cursor-pointer p-2 rounded-lg hover:bg-gray-50 transition-colors"
                                                        >
                                                            <input
                                                                type="radio"
                                                                name={option.id}
                                                                checked={selectedOptions[option.id] === choice.id}
                                                                onChange={() => handleSelectChange(option.id, choice.id)}
                                                                className="w-4 h-4 text-orange-600 focus:ring-orange-500"
                                                            />
                                                            <span className="text-sm flex-1">{choice.label}</span>
                                                            {choice.priceModifier != null && choice.priceModifier !== 0 && (
                                                                <span className="text-sm font-medium text-orange-600">
                                                                    {choice.priceModifier > 0 ? '+' : ''}{safeToFixed(choice.priceModifier, 2)}€
                                                                </span>
                                                            )}
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Multiselect option - checkboxes */}
                                        {option.type === 'multiselect' && option.choices && (
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm font-medium text-gray-700">{option.label}</span>
                                                    {option.maxSelections && (
                                                        <span className="text-xs text-gray-500">
                                                            (max {option.maxSelections})
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="space-y-1">
                                                    {option.choices.map((choice) => {
                                                        const currentValue = selectedOptions[option.id];
                                                        const isChecked = Array.isArray(currentValue) && currentValue.includes(choice.id);
                                                        const isDisabled = isMultiselectChoiceDisabled(option.id, choice.id, option.maxSelections);

                                                        return (
                                                            <label
                                                                key={choice.id}
                                                                className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${
                                                                    isDisabled
                                                                        ? 'opacity-50 cursor-not-allowed'
                                                                        : 'cursor-pointer hover:bg-gray-50'
                                                                }`}
                                                            >
                                                                <Checkbox
                                                                    checked={isChecked}
                                                                    disabled={isDisabled}
                                                                    onCheckedChange={() => handleMultiselectToggle(option.id, choice.id, option.maxSelections)}
                                                                />
                                                                <span className="text-sm flex-1">{choice.label}</span>
                                                                {choice.priceModifier != null && choice.priceModifier !== 0 && (
                                                                    <span className="text-sm font-medium text-orange-600">
                                                                        {choice.priceModifier > 0 ? '+' : ''}{safeToFixed(choice.priceModifier, 2)}€
                                                                    </span>
                                                                )}
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Quantity Selection - uses adaptive QuantitySelector */}
                    <div className="space-y-3 pt-2 border-t">
                        <QuantitySelector
                            product={product}
                            quantity={quantity}
                            onQuantityChange={setQuantity}
                            servings={servings}
                            onServingsChange={setServings}
                            selectedPortionSizeId={selectedPortionSizeId}
                            onPortionSizeChange={setSelectedPortionSizeId}
                        />

                        {/* Estimated price display */}
                        {estimatedPrice !== null && isSelectionValid() && (
                            <div className="text-center pt-2">
                                <span className="text-lg text-gray-600">
                                    {saleMode === 'UNIT_PRICED_BY_WEIGHT' || saleMode === 'WEIGHT_ESTIMATED'
                                        ? 'Estimation: '
                                        : 'Total: '
                                    }
                                </span>
                                <span className="text-xl font-bold text-orange-600">
                                    {saleMode === 'UNIT_PRICED_BY_WEIGHT' || saleMode === 'WEIGHT_ESTIMATED'
                                        ? `≈ ${safeToFixed(estimatedPrice, 2)}€`
                                        : `${safeToFixed(estimatedPrice, 2)}€`
                                    }
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={onClose}>
                        Annuler
                    </Button>
                    <Button
                        onClick={handleAdd}
                        disabled={!canAddToCart}
                        className="bg-orange-600 hover:bg-orange-700"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Ajouter au panier
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
